import {
  Box,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ExceptionConfigSectionProps } from '../utils/DataTypes';

const ExceptionConfigSection = ({
  title,
  categories,
  fieldLabels,
  formState,
  setFormState,
  isLoading,
  isDefault,
  canSave,
  onReset,
  onSave,
  hideBulkActions,
  selectedLayout,
  onLayoutChange,
  isLayoutLocked,
  layoutOptions,
}: ExceptionConfigSectionProps) => {
  const theme = useTheme();

  const toggleField = (field: string, checked: boolean) => {
    setFormState((prev: any) => {
      const current = prev.fields ?? [];
      return {
        ...prev,
        fields: checked ? [...current, field] : current.filter((f: string) => f !== field),
      };
    });
  };

  const selectAll = () => {
    setFormState({
      fields: categories.flatMap((c) => c.fields),
    });
  };

  const deselectAll = () => {
    setFormState({ fields: [] });
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {title}
          {isDefault && (
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: '0.75rem',
                color: 'information.main',
                fontWeight: 500,
              }}
            >
              (Using default configuration)
            </Typography>
          )}
        </Typography>

        <>
          {layoutOptions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small">
                <Select
                  value={selectedLayout}
                  onChange={(e) => onLayoutChange?.(e.target.value as string)}
                  sx={{
                    minWidth: 260,
                  }}
                >
                  {layoutOptions.map((l: any) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {!hideBulkActions && (
            <>
              {' '}
              <Button size="small" onClick={selectAll} variant="outlined">
                Select All
              </Button>
              <Button size="small" onClick={deselectAll} variant="outlined">
                Deselect All
              </Button>
              <Button size="small" onClick={onReset} variant="outlined">
                Reset to Default
              </Button>
            </>
          )}

          <Button variant="contained" size="small" onClick={onSave} disabled={isLoading || !canSave}>
            {isLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </>
      </Box>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          mt: 2,
          minHeight: 200,
        }}
      >
        {categories.map((cat, index) => (
          <Box
            key={cat.id}
            sx={{
              display: 'flex',
              backgroundColor: index % 2 ? alpha(theme.palette.action.hover, 0.04) : 'transparent',
              borderBottom: index < categories.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
            }}
          >
            <Box
              sx={{
                minWidth: 170,
                maxWidth: 170,
                p: 1,
                fontWeight: 'bold',
                borderRight: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              {cat.name}
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: 1,
                p: 1,
              }}
            >
              {cat.fields.map((field) => {
                const isChecked = formState.fields?.includes(field);
                return (
                  <Box
                    key={field}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      minWidth: 110,
                      p: '4px',
                      opacity: isLayoutLocked && !isChecked ? 0.6 : 1,
                      pointerEvents: isLayoutLocked ? 'none' : 'auto',
                    }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ padding: '0 0 2px 0' }}
                      checked={formState.fields?.includes(field)}
                      onChange={(e) => {
                        if (isLayoutLocked) return;
                        toggleField(field, e.target.checked);
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        lineHeight: 1.1,
                        wordBreak: 'break-word',
                        fontSize: '0.75rem',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {fieldLabels[field] || field}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default ExceptionConfigSection;
